import { invokeWithSessionRetry } from "@/lib/invoke-with-session-retry";
import { buildAiIdempotencyKey, deductBudget, ensureTokenBudget, toFunctionErrorMessage, type AiBudgetOptions, type AiUsageSnapshot } from "@/lib/ai-client-common";

export type AiAction = "example" | "redesign" | "test_mcq" | "test_mcq_answer" | "test_essay" | "read_essay_attachment" | "evaluate_essay";

type EssayAttachmentRequest = {
  mimeType: "image/png" | "image/jpeg" | "image/webp";
  imageBase64: string;
};

export type AiActionRequest = {
  action: AiAction;
  term: string;
  context: string;
  resourceId: string;
  pageNumber: number;
  studentAnswer?: string;
  confirmedAnswerText?: string | null;
  attemptId?: string;
  selectedIndex?: number;
  attachment?: EssayAttachmentRequest | null;
  idempotencyKey?: string;
};

export type AiActionResponse = {
  content: string;
  question?: { text: string; options?: string[]; correctIndex?: number };
  readback?: { extractedText: string; summary: string; confidence: "low" | "medium" | "high" };
  evaluation?: { score: string; highlights: string; missing: string; modelAnswer: string };
  fromCache: boolean;
  tokenCost: number;
  attemptId?: string | null;
  isCorrect?: boolean | null;
  correctIndex?: number | null;
  usage?: AiUsageSnapshot;
};

const ACTION_ESTIMATES: Record<AiAction, number> = {
  example: 800,
  redesign: 2000,
  test_mcq: 400,
  test_mcq_answer: 0,
  test_essay: 400,
  read_essay_attachment: 500,
  evaluate_essay: 900,
};

export async function requestAiAction(
  req: AiActionRequest,
  options?: Omit<AiBudgetOptions, "estimatedCost">,
): Promise<AiActionResponse> {
  ensureTokenBudget(options ? { ...options, estimatedCost: ACTION_ESTIMATES[req.action] ?? 800 } : undefined, ACTION_ESTIMATES[req.action] ?? 800);

  const { data, error } = await invokeWithSessionRetry<AiActionResponse>("ai-action", {
    body: {
      ...req,
      idempotencyKey:
        req.idempotencyKey ??
        buildAiIdempotencyKey({
          feature: "ai-action",
          action: req.action,
          resourceId: req.resourceId,
          pageNumber: req.pageNumber,
          term: req.term.trim(),
          attemptId: req.attemptId ?? "",
          selectedIndex: req.selectedIndex ?? null,
          studentAnswer: req.studentAnswer ?? "",
          confirmedAnswerText: req.confirmedAnswerText ?? "",
          attachmentPreview: req.attachment ? `${req.attachment.mimeType}:${req.attachment.imageBase64.slice(0, 48)}` : "",
        }),
    },
  });
  if (error || !data) {
    throw new Error(await toFunctionErrorMessage(error, "Failed to run AI action"));
  }

  const cost = typeof data.tokenCost === "number" ? data.tokenCost : ACTION_ESTIMATES[req.action] ?? 800;
  deductBudget(options ? { ...options, estimatedCost: cost } : undefined, cost);
  return data as AiActionResponse;
}
